import CheckIcon from '@mui/icons-material/Check';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  FormControlLabel,
  IconButton,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface SaveBadgeProps {
  isError?: boolean;
}

export const BillingBoxWrapper = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  overflow: 'hidden',

  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4)
  },
  [theme.breakpoints.up('md')]: {
    paddingLeft: '5%',
    paddingRight: '5%'
  },
  [theme.breakpoints.up('xl')]: {
    paddingLeft: '15%',
    paddingRight: '15%'
  }
}));

export const SmallIconButton = styled(IconButton)(() => ({
  width: '25px',
  height: '25px'
}));

export const SectionWrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  width: '100%'
}));

export const StyledButton = styled(LoadingButton)(() => ({
  backgroundColor: '#175CE5',
  borderRadius: '8px',
  textTransform: 'none',
  padding: '8px 12px',
  color: '#FFFFFF',
  '&:hover': {
    backgroundColor: '#003BB2'
  },
  '&.Mui-disabled': {
    backgroundColor: '#003399',
    opacity: 0.6,
    cursor: 'not-allowed',
    color: '#FFFFFF'
  },
  '& .MuiCircularProgress-root': {
    color: '#FFFFFF'
  }
}));

export const StyledOutlinedButton = styled(LoadingButton)(() => ({
  borderRadius: '8px',
  textTransform: 'none',
  padding: '8px 12px',
  '& .MuiCircularProgress-root': {
    color: '#175CE5'
  }
}));

export const ColumnWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
}));

export const StyledRadioGroup = styled(RadioGroup)(() => ({
  display: 'flex',
  gap: 16,
  flexDirection: 'row'
}));

export const PlanOption = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ theme, selected }) => ({
  flex: 1,
  margin: 0,
  padding: theme.spacing(2),
  border: '2px solid',
  borderColor: selected ? '#0044CC' : theme.palette.grey[300],
  borderRadius: '8px',
  alignItems: 'flex-start',
  maxHeight: '88px',
  '& .MuiFormControlLabel-label': {
    width: '100%'
  }
}));

export const SaveBadge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isError'
})<SaveBadgeProps>(({ isError }) => ({
  borderRadius: '4px',
  border: isError
    ? '1px solid var(--Color-Error-100, #FAC7CF)'
    : '1px solid var(--Color-Info-100, #C3DEF1)',
  background: '#EEF6FB',
  padding: '2px 6px',
  fontSize: '12px',
  fontWeight: 500,
  color: isError ? 'var(--Text-error, #C91D3A)' : 'var(--Text-info, #0A578F)'
}));

export const GridWrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr;

  gap: 24px;

  @media (min-width: 900px) {
    grid-template-columns: 2fr 1fr;
  }
`;

export const ColumnWrapperBox = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const SeatsCard = styled(Box)`
  padding: 20px;
  border-radius: 8px;
  border: 1px solid var(--Border-primary, #d6d6d6);
`;

export const RowBetween = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export const RightAlignedBox = styled(Box)`
  display: flex;
  justify-content: flex-end;
`;

export const PrimaryButton = styled(LoadingButton)({
  backgroundColor: '#175ce5',
  borderRadius: '8px',
  marginTop: '16px',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#003BB2'
  },
  '& .MuiCircularProgress-root': {
    color: '#FFFFFF'
  }
});

export const GradientBox = styled(Box)`
  padding: 16px;
  border-radius: 8px;
  background: linear-gradient(
    220deg,
    rgba(210, 218, 247, 0.5) 9.5%,
    rgba(233, 233, 245, 0.5) 39.5%,
    rgba(207, 243, 225, 0.5) 70.48%,
    rgba(194, 243, 220, 0.5) 99.62%
  );
`;

export const BorderedBox = styled(Box)`
  padding: 16px;
  border: 1px solid #d6d6d6;
  border-radius: 8px;
`;

export const DisclaimerBox = styled(Box)`
  display: flex;
  flex-direction: row;
  background: var(--Background-surface-brand-light, #e5eeff);
  padding: 12px;
  margin-top: 16px;
  gap: 8px;
  border-radius: 4px;
  align-items: flex-start;
`;

export const RowBetweenBold = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 8px; /* mt={1} */
  font-weight: bold;
`;

export const FeatureItem = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 8px; /* mb={1} */
`;

export const FeatureCheckIcon = styled(CheckIcon)`
  color: #737373;
  margin-right: 8px; /* mr={1} */
  font-size: 1rem; /* fontSize="small" */
`;

export const PricingWrapper = styled(Box)(({ theme }) => ({
  textAlign: 'left',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2)
}));

export const SeatManagementWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

export const StyledRow = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%'
}));

export const SeatSelectorWrapper = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #ddd',
  borderRadius: '6px',
  overflow: 'hidden'
}));

export const SeatSelectorButtonIcon = styled(IconButton)(() => ({
  padding: 4,
  '&.Mui-disabled': {
    opacity: 0.5
  }
}));

export const SeatSelectorInput = styled(TextField)(() => ({
  '& input': {
    textAlign: 'center',
    width: '40px',
    padding: '6px',
    MozAppearance: 'textfield'
  },
  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
    {
      WebkitAppearance: 'none',
      margin: 0
    },
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    '& fieldset': {
      borderTop: 'none',
      borderBottom: 'none'
    }
  }
}));

export const SeatMinusDialogContent = styled(Typography)(() => ({
  // equivalent to mt: 2
  fontWeight: 400,
  fontSize: '14px',
  textAlign: 'center'
}));
